import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AvatarNotificationModalActions } from './notification-modal.actions';
import { AvatarNotificationService } from './notification-modal.service';

/**
 * Effects for avatar notification modal feature.
 * Handles side effects like API calls.
 *
 * @exports
 * @class AvatarNotificationModalEffects
 */
@Injectable()
export class AvatarNotificationModalEffects extends AvatarNotificationService {
  private readonly actions$ = inject(Actions);

  /**
   * Effect to check if avatar notification modal should be shown.
   * Calls GET /avatar/notification/should-show endpoint.
   */
  checkShouldShow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarNotificationModalActions.checkShouldShow),
      switchMap(() =>
        this.checkShouldShow().pipe(
          map((response) =>
            AvatarNotificationModalActions.checkShouldShowSuccess({
              shouldShow: response.data.shouldShow,
              reason: response.data.reason
            })
          ),
          catchError((error) =>
            of(
              AvatarNotificationModalActions.checkShouldShowFailed({
                error: error.message ?? 'Failed to check notification status'
              })
            )
          )
        )
      )
    )
  );

  /**
   * Effect to record user interaction with avatar notification modal.
   * Calls POST /avatar/notification/interaction endpoint.
   */
  recordInteraction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarNotificationModalActions.recordInteraction),
      switchMap(({ action }) =>
        this.recordInteraction(action).pipe(
          map((response) =>
            AvatarNotificationModalActions.recordInteractionSuccess({
              data: response.data
            })
          ),
          catchError((error) =>
            of(
              AvatarNotificationModalActions.recordInteractionFailed({
                error: error.message ?? 'Failed to record interaction'
              })
            )
          )
        )
      )
    )
  );
}
